import { useEffect, useMemo, useState } from 'react';
import { Edit, Trash2, Key, Phone } from 'lucide-react';
import {
  Avatar,
  Badge as DsBadge,
  Button as DsButton,
  ConfirmModal,
  Form,
  Icon,
  Input as DsInput,
  Modal,
} from '@shared/design-system';
import { usePageHeaderActions, useAuth } from '@/contexts';
import { Can } from '../components/auth/Can';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { ApiError, TR_MESSAGES, mapProblemToFormErrors, toastFromApiError } from '@shared/api-errors';
import { formatDateTime } from '@/lib/utils';
import { type User, type CreateUserRequest, type UpdateUserRequest } from '../services/userService';
import {
  useUsersList,
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
  useResetUserPassword,
  useAssignRolesToUser,
} from '@/hooks/queries/useUsers';
import { useAllRoles } from '@/hooks/queries/useRoles';

const initialFormData = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  phone: '',
  isActive: true,
};

export function Users() {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  // Debounced search — triggers the query with the stable value.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const pageSize = 10;

  // Role data (table badges + assignment checkboxes) comes from roles.view-gated
  // endpoints. Skip those fetches when the user can't view roles so the page
  // doesn't fire a 403 + "Forbidden" toast (e.g. Manager has users.* but not roles.view).
  const { hasPermission } = useAuth();
  const canViewRoles = hasPermission('roles.view');

  const usersQuery = useUsersList(page, pageSize, debouncedSearch, canViewRoles);
  const rolesQuery = useAllRoles(canViewRoles);

  // Form-level errors for the user create/edit modal. Populated by onError
  // when the backend returns VALIDATION_FAILED or DUPLICATE_EMAIL.
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // mutationCache.onError in App.tsx bails when the mutation registers its own
  // onError (this handler), so we render the TR fallback toast ourselves for
  // cases we don't handle inline (including raw axios errors from legacy-shape
  // 4xx responses and network failures).
  const handleUserSaveError = (error: unknown) => {
    if (!(error instanceof ApiError)) {
      toastFromApiError(error, (p) => toast(p));
      return;
    }
    if (error.code === 'VALIDATION_FAILED') {
      setFormErrors(mapProblemToFormErrors(error));
      return;
    }
    if (error.code === 'DUPLICATE_EMAIL') {
      setFormErrors({ email: TR_MESSAGES.DUPLICATE_EMAIL });
      return;
    }
    if (error.code === 'TENANT_ACCESS_DENIED') {
      toast({
        title: 'Hata',
        description: TR_MESSAGES.TENANT_ACCESS_DENIED,
        variant: 'destructive',
      });
      return;
    }
    toastFromApiError(error, (p) => toast(p));
  };

  const createUser = useCreateUser({ onError: handleUserSaveError });
  const updateUser = useUpdateUser({ onError: handleUserSaveError });
  const deleteUser = useDeleteUser();
  const resetPassword = useResetUserPassword();
  const assignRoles = useAssignRolesToUser();

  const users = usersQuery.data?.items ?? [];
  const totalCount = usersQuery.data?.totalCount ?? 0;
  const userRolesMap = usersQuery.data?.rolesMap ?? {};
  const roles = rolesQuery.data ?? [];
  const isLoading = usersQuery.isPending;
  const saving = createUser.isPending || updateUser.isPending || assignRoles.isPending;

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState(initialFormData);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);

  const [showResetPassword, setShowResetPassword] = useState(false);
  const [userToReset, setUserToReset] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const totalPages = Math.ceil(totalCount / pageSize);

  usePageHeaderActions(
    useMemo(
      () => (
        <Can do="users.create">
          <DsButton
            variant="primary"
            size="sm"
            icon="plus"
            onClick={() => {
              setFormErrors({});
              setEditingUser(null);
              setFormData(initialFormData);
              setSelectedRoleIds([]);
              setShowModal(true);
            }}
          >
            Yeni Kullanıcı
          </DsButton>
        </Can>
      ),
      [],
    ),
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // List-load errors flow to queryCache.onError in App.tsx (TR-mapped toast).

  const handleOpenModal = (user?: User) => {
    setFormErrors({});
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        password: '',
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone || '',
        isActive: user.isActive,
      });
      // Roles for this user already fetched by the list query — reuse the cached map.
      setSelectedRoleIds((userRolesMap[user.id] ?? []).map((r) => r.id));
    } else {
      setEditingUser(null);
      setFormData(initialFormData);
      setSelectedRoleIds([]);
    }
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});
    try {
      let userId: string;
      if (editingUser) {
        const updateData: UpdateUserRequest = {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone || undefined,
          isActive: formData.isActive,
        };
        await updateUser.mutateAsync({ id: editingUser.id, data: updateData });
        userId = editingUser.id;
      } else {
        const createData: CreateUserRequest = {
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone || undefined,
          isActive: formData.isActive,
        };
        const created = await createUser.mutateAsync(createData);
        userId = created.id;
      }

      try {
        await assignRoles.mutateAsync({ userId, roleIds: selectedRoleIds });
      } catch {
        toast({
          title: 'Uyarı',
          description: 'Kullanıcı kaydedildi ancak roller atanırken bir hata oluştu.',
          variant: 'destructive',
        });
        setShowModal(false);
        return;
      }

      toast({
        title: 'Başarılı',
        description: editingUser ? 'Kullanıcı başarıyla güncellendi.' : 'Yeni kullanıcı başarıyla oluşturuldu.',
        variant: 'success',
      });
      setShowModal(false);
    } catch {
      // Errors handled by mutation.onError (field errors / code branching)
      // or by the mutationCache fallback toast. Modal stays open so the user
      // can correct validation issues inline.
    }
  };

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await deleteUser.mutateAsync(userToDelete.id);
      toast({
        title: 'Başarılı',
        description: 'Kullanıcı başarıyla silindi.',
        variant: 'success',
      });
    } catch {
      // Error toast rendered by mutationCache fallback with TR-mapped message.
    } finally {
      setShowDeleteConfirm(false);
      setUserToDelete(null);
    }
  };

  const handleResetPasswordClick = (user: User) => {
    setUserToReset(user);
    setNewPassword('');
    setConfirmPassword('');
    setShowResetPassword(true);
  };

  const handleConfirmResetPassword = async () => {
    if (!userToReset || !newPassword) return;
    if (newPassword !== confirmPassword) {
      toast({
        title: 'Hata',
        description: 'Şifreler eşleşmiyor.',
        variant: 'destructive',
      });
      return;
    }
    if (newPassword.length < 6) {
      toast({
        title: 'Hata',
        description: 'Şifre en az 6 karakter olmalı.',
        variant: 'destructive',
      });
      return;
    }
    try {
      await resetPassword.mutateAsync({ id: userToReset.id, newPassword });
      toast({
        title: 'Başarılı',
        description: 'Kullanıcı şifresi başarıyla sıfırlandı.',
        variant: 'success',
      });
      setShowResetPassword(false);
      setUserToReset(null);
    } catch {
      // Error toast rendered by mutationCache fallback with TR-mapped message.
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Filters row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: '0 0 360px' }}>
          <DsInput
            size="sm"
            icon="search"
            placeholder="Kullanıcı ara (ad, email)..."
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: 'var(--dg-text-3)' }}>
          {totalCount} kullanıcı · sayfa {page}/{Math.max(1, totalPages)}
        </span>
      </div>

      {/* Table */}
      <div
        style={{
          background: 'white',
          border: '1px solid var(--dg-border)',
          borderRadius: 'var(--dg-r-lg)',
          overflow: 'hidden',
          boxShadow: 'var(--dg-shadow-sm)',
        }}
      >
        {isLoading && users.length === 0 ? (
          <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[...Array(5)].map((_, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-32" />
                </div>
                <Skeleton className="h-6 w-16" />
              </div>
            ))}
          </div>
        ) : users.length === 0 ? (
          <div style={{ padding: '64px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <Icon name="users" size={32} style={{ color: 'var(--dg-text-3)' }} />
            <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--dg-text-1)' }}>Kullanıcı bulunamadı</div>
            <div style={{ fontSize: 13, color: 'var(--dg-text-3)' }}>
              {search ? 'Arama kriterlerinize uygun kullanıcı yok' : 'Henüz kullanıcı eklenmemiş'}
            </div>
            {!search && (
              <Can do="users.create">
                <DsButton
                  variant="primary"
                  size="sm"
                  icon="plus"
                  onClick={() => handleOpenModal()}
                  style={{ marginTop: 8 }}
                >
                  İlk Kullanıcıyı Ekle
                </DsButton>
              </Can>
            )}
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', minWidth: 720, borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--dg-surface)', borderBottom: '1px solid var(--dg-border)' }}>
                  {['Kullanıcı', 'İletişim', 'Rol', 'Durum', 'Son Giriş', ''].map((h, i) => (
                    <th
                      key={i}
                      style={{
                        padding: '10px 12px',
                        textAlign: 'left',
                        fontSize: 11.5,
                        fontWeight: 600,
                        color: 'var(--dg-text-3)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((user, i) => (
                  <tr
                    key={user.id}
                    style={{
                      borderBottom: i < users.length - 1 ? '1px solid var(--dg-border)' : 'none',
                    }}
                  >
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar name={user.fullName || `${user.firstName} ${user.lastName}`} size={32} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 500, color: 'var(--dg-text-1)' }}>{user.fullName}</div>
                          <div style={{ fontSize: 12, color: 'var(--dg-text-3)' }}>{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {user.phone ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--dg-text-2)' }}>
                          <Phone className="size-3.5" style={{ color: 'var(--dg-text-3)' }} />
                          {user.phone}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--dg-text-3)' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {(userRolesMap[user.id] || []).length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {userRolesMap[user.id].map((role) => (
                            <DsBadge key={role.id} tone="neutral" size="sm">
                              {role.name}
                            </DsBadge>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--dg-text-3)' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <DsBadge tone={user.isActive ? 'ok' : 'neutral'} size="sm">
                        {user.isActive ? 'Aktif' : 'Pasif'}
                      </DsBadge>
                    </td>
                    <td style={{ padding: '12px', color: 'var(--dg-text-3)', fontSize: 12.5, whiteSpace: 'nowrap' }}>
                      {user.lastLoginAt ? formatDateTime(user.lastLoginAt) : '—'}
                    </td>
                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: 4 }}>
                        <Can do="users.edit">
                          <Button variant="ghost" size="icon-sm" onClick={() => handleOpenModal(user)} title="Düzenle">
                            <Edit className="size-4" />
                          </Button>
                        </Can>
                        <Can do="users.edit">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => handleResetPasswordClick(user)}
                            title="Şifre Sıfırla"
                            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                          >
                            <Key className="size-4" />
                          </Button>
                        </Can>
                        <Can do="users.delete">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => handleDeleteClick(user)}
                            title="Sil"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </Can>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 16px',
                  borderTop: '1px solid var(--dg-border)',
                  background: 'var(--dg-surface)',
                  fontSize: 12.5,
                  color: 'var(--dg-text-3)',
                }}
              >
                <span>Sayfa {page} / {totalPages}</span>
                <div style={{ flex: 1 }} />
                <DsButton
                  variant="secondary"
                  size="sm"
                  icon="chevleft"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Önceki
                </DsButton>
                <DsButton
                  variant="secondary"
                  size="sm"
                  iconRight="chevright"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Sonraki
                </DsButton>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create/Edit Modal (DS Modal + Form) */}
      <Modal
        open={showModal}
        onOpenChange={(open) => { setShowModal(open); if (!open) setFormErrors({}); }}
        size="sm"
      >
        <Modal.Header
          title={editingUser ? 'Kullanıcıyı Düzenle' : 'Yeni Kullanıcı'}
          description={editingUser ? 'Kullanıcı bilgilerini güncelleyin' : 'Yeni bir kullanıcı oluşturun'}
        />
        <Modal.Body>
          <Form id="user-form" onSubmit={handleSubmit}>
            <Form.Row>
              <Form.Field label="Ad" htmlFor="user-firstName" required>
                <Input
                  id="user-firstName"
                  required
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                />
              </Form.Field>
              <Form.Field label="Soyad" htmlFor="user-lastName" required>
                <Input
                  id="user-lastName"
                  required
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                />
              </Form.Field>
            </Form.Row>

            <Form.Field label="E-posta" htmlFor="user-email" required error={formErrors.email}>
              <Input
                id="user-email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value });
                  if (formErrors.email) setFormErrors({ ...formErrors, email: '' });
                }}
              />
            </Form.Field>

            {!editingUser && (
              <Form.Field label="Şifre" htmlFor="user-password" required helperText="En az 6 karakter">
                <Input
                  id="user-password"
                  type="password"
                  required
                  minLength={6}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
              </Form.Field>
            )}

            <Form.Field label="Telefon" htmlFor="user-phone">
              <Input
                id="user-phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Form.Field>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Durum</Label>
                <p className="text-sm text-slate-500">Kullanıcı sisteme giriş yapabilir</p>
              </div>
              <Switch
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              />
            </div>

            {roles.length > 0 && (
              <div className="space-y-2">
                <Label>Roller</Label>
                <div className="space-y-2 rounded-md border border-slate-200 p-3">
                  {roles.map((role) => (
                    <label key={role.id} className="flex items-start gap-3 cursor-pointer">
                      <Checkbox
                        checked={selectedRoleIds.includes(role.id)}
                        onCheckedChange={(checked) => {
                          setSelectedRoleIds(prev =>
                            checked
                              ? [...prev, role.id]
                              : prev.filter(id => id !== role.id)
                          );
                        }}
                        className="mt-0.5"
                      />
                      <div className="space-y-0.5">
                        <span className="text-sm font-medium text-slate-700">{role.name}</span>
                        {role.description && (
                          <p className="text-xs text-slate-500">{role.description}</p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
            İptal
          </Button>
          <Button type="submit" form="user-form" disabled={saving}>
            {saving ? 'Kaydediliyor...' : editingUser ? 'Güncelle' : 'Oluştur'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation (DS ConfirmModal) */}
      <ConfirmModal
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Kullanıcıyı Sil"
        description={
          <>
            <strong>{userToDelete?.fullName}</strong> adlı kullanıcıyı silmek istediğinize emin misiniz?
            Bu işlem geri alınamaz.
          </>
        }
        confirmText="Sil"
        cancelText="İptal"
        variant="destructive"
        loading={deleteUser.isPending}
        onConfirm={handleConfirmDelete}
      />

      {/* Reset Password Modal (DS Modal + Form) */}
      <Modal open={showResetPassword} onOpenChange={setShowResetPassword} size="sm">
        <Modal.Header
          title="Şifre Sıfırla"
          description={<><strong>{userToReset?.fullName}</strong> için yeni şifre belirleyin</>}
        />
        <Modal.Body>
          <Form id="reset-form" onSubmit={(e) => { e.preventDefault(); handleConfirmResetPassword(); }}>
            <Form.Field label="Yeni Şifre" htmlFor="reset-newPassword" required>
              <Input
                id="reset-newPassword"
                type="password"
                required
                minLength={6}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </Form.Field>
            <Form.Field label="Şifre Tekrar" htmlFor="reset-confirmPassword" required>
              <Input
                id="reset-confirmPassword"
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </Form.Field>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="outline" onClick={() => setShowResetPassword(false)}>
            İptal
          </Button>
          <Button type="submit" form="reset-form" disabled={resetPassword.isPending}>
            {resetPassword.isPending ? 'Sıfırlanıyor...' : 'Sıfırla'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
